import dayjs from 'dayjs';
import isSafeInteger from 'lodash/isSafeInteger';

import {
  ConvertableToDateTime,
  DateRange,
  OptionalTimeRange,
} from '@cast/shared-types';

export const getDateType = (
  timestamp: ConvertableToDateTime
): 'date' | 'unix' | 'iso' => {
  if (typeof timestamp === 'number') {
    return 'unix';
  }

  if (timestamp instanceof Date) {
    return 'date';
  }

  const _timestamp = +timestamp;
  if (isSafeInteger(_timestamp)) {
    return 'unix';
  }

  return 'iso';
};

export const anyTimestampToUnix = (
  timestamp: ConvertableToDateTime
): number => {
  const type = getDateType(timestamp);

  switch (type) {
    case 'date':
      return dayjs(timestamp).unix();
    case 'unix':
      return +timestamp;
    case 'iso':
      return dayjs(timestamp).unix();
  }
};

export const convertToDateType = (
  timestamp: ConvertableToDateTime,
  type: 'date' | 'unix' | 'iso'
): string | number | Date => {
  const _unix = anyTimestampToUnix(timestamp);

  switch (type) {
    case 'date':
      return dayjs.unix(_unix).toDate();
    case 'unix':
      return _unix;
    case 'iso':
      return dayjs.unix(_unix).toISOString();
  }
};

/**
 * Adjusts date range end to next day midnight for API compatibility with day-level granularity
 * Example: [2023-01-01 12:00, 2023-01-02 23:59] → [2023-01-01 00:00, 2023-01-03 00:00]
 *
 * @param alignToHour - When true (default), truncates UTC timestamps to whole hours for APIs
 * that require whole-hour boundaries. Set to false for APIs that support minute-level precision,
 * which is required for timezones with non-whole-hour UTC offsets (e.g. IST UTC+5:30).
 */
export const adjustRangeForDayGranularity = <
  T extends dayjs.Dayjs | Date | string
>(
  range: DateRange | OptionalTimeRange<T>,
  timezone = dayjs.tz.guess(),
  alignToHour = true
): DateRange => {
  // FIXME(adomas): remove me once fully deprecated DateRange type https://castai.atlassian.net/browse/UI-4114
  const _range = Array.isArray(range)
    ? range
    : [dayjs(range.from), dayjs(range.to)];

  const start = _range[0];
  let end = _range[1];

  if (!end.isSame(start, 'day')) {
    end = dayjs.tz(end, timezone).startOf('d').add(1, 'd');
  } else {
    end = dayjs.tz(start, timezone).startOf('d').add(1, 'd');
  }

  const startUtc = dayjs.tz(start, timezone).startOf('d').utc();
  const endUtc = end.utc();

  if (alignToHour) {
    return [startUtc.startOf('h'), endUtc.startOf('h')];
  }
  return [startUtc, endUtc];
};
